import { getAssignedSeatPosition } from "../models/assignedSeating";
import type { AssignedSeatingSeat } from "../models/assignedSeating";

const FALLBACK_SEATS_PER_ROW = 10;

const positiveInteger = (value?: number) =>
  Number.isFinite(value) && value && value > 0 ? Math.round(value) : undefined;

export interface SeatingMapGeometry {
  columns: number;
  rows: number;
  positions: Map<string, { x: number; y: number }>;
  rowLabels: Map<number, string>;
}

export const buildSeatingMapGeometry = (
  seats: AssignedSeatingSeat[]
): SeatingMapGeometry => {
  const positions = new Map<string, { x: number; y: number }>();
  const rowLabels = new Map<number, string>();
  const rowYByLabel = new Map<string, number>();
  const occupiedPositions = new Set<string>();
  let columns = 1;
  let rows = 1;

  seats.forEach((seat, index) => {
    const {
      row,
      number,
      x: sourceX,
      y: sourceY,
    } = getAssignedSeatPosition(seat);
    const x = positiveInteger(sourceX);
    const y = positiveInteger(sourceY);

    if (y && row && !rowLabels.has(y)) {
      rowLabels.set(y, row);
      rowYByLabel.set(row, y);
    }

    if (x && y) {
      positions.set(seat.seatKey, { x, y });
      occupiedPositions.add(`${x}:${y}`);
      columns = Math.max(columns, x);
      rows = Math.max(rows, y);
      return;
    }

    const fallbackRow =
      row || `__fallback-${Math.floor(index / FALLBACK_SEATS_PER_ROW)}`;
    const fallbackY =
      y || rowYByLabel.get(fallbackRow) || rows + (positions.size ? 1 : 0);
    if (!rowYByLabel.has(fallbackRow)) {
      rowYByLabel.set(fallbackRow, fallbackY);
      if (row) {
        rowLabels.set(fallbackY, row);
      }
    }

    let fallbackX =
      x ||
      positiveInteger(Number(number)) ||
      (index % FALLBACK_SEATS_PER_ROW) + 1;
    while (occupiedPositions.has(`${fallbackX}:${fallbackY}`)) {
      fallbackX += 1;
    }

    positions.set(seat.seatKey, { x: fallbackX, y: fallbackY });
    occupiedPositions.add(`${fallbackX}:${fallbackY}`);
    columns = Math.max(columns, fallbackX);
    rows = Math.max(rows, fallbackY);
  });

  return { columns, rows, positions, rowLabels };
};

export const clampSeatingMapZoom = (zoomPercent: number) =>
  Math.min(200, Math.max(50, zoomPercent));

export const adjustSeatingMapZoom = (
  zoomPercent: number,
  direction: "in" | "out"
) => clampSeatingMapZoom(zoomPercent + (direction === "in" ? 10 : -10));

export const formatSeatingHoldCountdown = (remainingSeconds: number) => {
  const minutes = Math.floor(Math.max(0, remainingSeconds) / 60);
  const seconds = Math.max(0, remainingSeconds) % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};
